import {
  Box,
  Flex,
  Text,
  Input,
  Button,
  FormLabel,
  InputGroup,
  FormControl,
  FormHelperText,
  FormErrorMessage,
  InputLeftElement,
} from '@chakra-ui/react'
import * as Yup from 'yup'
import { useFormik } from 'formik'
import PropTypes from 'prop-types'
import React, { useEffect, useState } from 'react'
import icon from '../../../utils/assets/ilus-codigo.svg'
import { REPOSITORY } from '../../../api/repository/Repository'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useLoadingContext } from '../../../utils/hooks/Context/LoadingContext'
import { faCheck, faWarning, faLock } from '@fortawesome/free-solid-svg-icons'

const KEY = 'one'

export function StepOne({ onNextStep, step }) {
  const { handleOpen: handleOpenLoading, handleClose: handleCLoseLoading } =
    useLoadingContext()

  const localData = REPOSITORY.local.company.getUserData()
  const [validCode, setValidCode] = useState(false)
  const [updateTime, setUpdateTime] = useState(0)
  const [time, setTime] = useState(3)

  useEffect(() => {
    const interval = setInterval(() => {
      setTime((old) => {
        if (old <= 0) return 0
        else return old - 1
      })
    }, [1 * 60 * 1000])

    return () => clearInterval(interval)
  }, [updateTime])

  const formik = useFormik({
    initialValues: {
      code: step.code,
    },
    validationSchema: Yup.object({
      code: Yup.string()
        .required('Ingresa un código')
        .length(6, 'Código no valido'),
    }),
    onSubmit: (values) => {
      if (validCode) {
        onNextStep(KEY, values)
      }
    },
  })

  useEffect(() => {
    if (String(formik.values.code).trim().length === 6) {
      handleOpenLoading()
      REPOSITORY.wsEmpresa.password
        .verifyCodLoginEmpresa(String(formik.values.code).trim())
        .then((success) => {
          if (success.data === true) {
            setValidCode(true)
          } else {
            setValidCode(false)
          }
        })
        .catch(() => {
          setValidCode(false)
        })
        .finally(() => {
          handleCLoseLoading()
        })
    } else {
      setValidCode(false)
    }
  }, [formik.values.code])

  const validateSuccessInput = (key) => {
    return (
      formik.touched[key] &&
      formik.errors[key] === undefined &&
      formik.values[key] !== ''
    )
  }

  const sendCode = () => {
    handleOpenLoading()
    REPOSITORY.wsEmpresa.password
      .reSendCodLoginEmpresa()
      .then(() => {
        setTime(3)
        setUpdateTime(updateTime + 1)
        setValidCode(false)
      })
      .catch(() => {
        alert('Error to re-send code.')
      })
      .finally(() => {
        handleCLoseLoading()
      })
  }

  const formatEmail = (email) => {
    const data = String(email || '').split('@')
    const _start = String(data[0]).slice(0, 1)
    const middle = String(data[0]).slice(-2)
    const end = data[1] ? data[1] : ''
    return `${_start}*******${middle}@${end}`
  }

  return (
    <Flex
      w="100%"
      justifyContent="center"
      pt={{ base: 0, sm: 0, md: 70 }}
      px={{ base: 0, sm: 0, md: 50 }}
    >
      <Box width={{ base: '100%', sm: '100%', md: 550 }}>
        <Box mb={10}>
          <img src={icon} alt="password" />
          <br />
          <Text textAlign="start" fontSize="2xl" mb={25} lineHeight={7}>
            Es necesario validar tu identidad.
          </Text>
          <Text textAlign="start" mb={2} fontSize="small">
            {`Te enviamos un código de identidad portal empresas a ${formatEmail(
              localData?.user?.sEmailMembership,
            )} para verificar que eres tú.`}
          </Text>
        </Box>
        <form onSubmit={formik.handleSubmit}>
          <FormControl
            mb={3}
            isInvalid={
              (formik.touched.code && formik.errors.code) ||
              (!validCode && formik.touched.code)
            }
          >
            <FormLabel fontWeight={600} fontSize={13} mb={1}>
              Código de identidad portal empresas
            </FormLabel>
            <InputGroup>
              <InputLeftElement>
                <FontAwesomeIcon size="lg" icon={faLock} color={'#898989'} />
              </InputLeftElement>
              <Input
                id="code"
                maxLength={6}
                borderRadius={3}
                disabled={validCode}
                backgroundColor="#FFFFFF"
                placeholder="0-0-0-0-0-0"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.code}
                borderWidth={0}
                boxShadow={
                  validateSuccessInput('code')
                    ? '0 0 0 1px #09825D'
                    : '0px 1px 2px 0px #37415114'
                }
              />
            </InputGroup>
            <FormErrorMessage>
              <FontAwesomeIcon
                size="sm"
                icon={faWarning}
                color={'#E53E3E'}
                style={{ marginRight: 5 }}
              />
              {formik.errors.code || 'Código no valido'}
            </FormErrorMessage>
            {validCode && (
              <FormHelperText color="#09825D">
                <FontAwesomeIcon
                  size="sm"
                  icon={faCheck}
                  color={'#09825D'}
                  style={{ marginRight: 5 }}
                />
                Verificación exitosa.
              </FormHelperText>
            )}
          </FormControl>
          <Button
            _disabled={{
              backgroundColor: '#DDDDDD',
              borderColor: '#DDDDDD',
              color: '#9A9A9A',
              _hover: {
                backgroundColor: '#DDDDDD',
              },
            }}
            _hover={{ backgroundColor: '#74BF00' }}
            disabled={!validateSuccessInput('code') || !validCode}
            borderRadius={50}
            type="submit"
            height={50}
            mt={4}
          >
            Continuar
          </Button>
          {!validCode ? (
            <>
              <Text
                textAlign="center"
                mt={7}
                fontWeight="normal"
                display="flex"
                justifyContent="center"
              >
                Tu código caduca en:&nbsp;<Text>{`${time} minutos.`}</Text>
              </Text>
              <Flex justifyContent="center" mt={5}>
                <Button
                  width={300}
                  onClick={sendCode}
                  _disabled={{
                    backgroundColor: 'transparent',
                    borderColor: 'transparent',
                    color: '#9A9A9A',
                    boxShadow: '0px 2px 26px transparent',
                    _hover: {
                      backgroundColor: 'transparent',
                    },
                  }}
                  disabled={!time <= 0}
                  borderRadius={50}
                  type="submit"
                  height={50}
                  mt={4}
                  _hover={{ backgroundColor: '#74BF00' }}
                >
                  Reenviar código
                </Button>
              </Flex>
            </>
          ) : null}
        </form>
      </Box>
    </Flex>
  )
}

StepOne.propTypes = {
  onNextStep: PropTypes.func,
  step: PropTypes.shape({
    code: PropTypes.string,
  }),
}
